/**
 * Homepage "Shop by style" scroller (<s-style-scroller>).
 * The section is tall and its full-screen photo sticky, so scrolling through it steps the active
 * style from first to last on every screen size: the photo cross-fades and that style's words pop up over it.
 */
class StyleScroller extends HTMLElement {
  #items = [];
  #images = [];
  #active = 0;
  #ticking = false;

  connectedCallback() {
    this.#items = [...this.querySelectorAll('[data-ss-item]')];
    this.#images = [...this.querySelectorAll('[data-ss-image]')];
    this.track = this.querySelector('[data-ss-track]');
    this.progress = this.querySelector('[data-ss-progress]');
    this.cta = this.querySelector('[data-ss-cta]');

    // Only the active style's words are visible and clickable; keyboard focus moves to a style and shows it.
    this.#items.forEach((item, index) => {
      item.addEventListener('focus', () => this.#set(index));
    });

    this.onScroll = () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#ticking = false;
        this.#fromScroll();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    // Theme editor: selecting one of the photos shows it, since only the active style's photo is visible.
    this.onEditorSelect = (event) => {
      const image = event.target instanceof Element ? event.target.closest('[data-ss-image]') : null;
      const index = image ? this.#images.indexOf(image) : -1;
      if (index >= 0) this.#set(index);
    };
    document.addEventListener('shopify:block:select', this.onEditorSelect);
    this.#set(0, true);
    this.#fromScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('shopify:block:select', this.onEditorSelect);
  }

  #fromScroll() {
    if (!this.track || this.#items.length === 0) return;
    const rect = this.track.getBoundingClientRect();
    const distance = this.track.offsetHeight - window.innerHeight;
    if (distance <= 0) return;
    const progress = Math.min(Math.max(-rect.top / distance, 0), 0.9999);
    this.#set(Math.floor(progress * this.#items.length));
  }

  #set(index, force = false) {
    if (index === this.#active && !force) return;
    this.#active = index;
    this.#items.forEach((item, i) => item.toggleAttribute('data-active', i === index));
    this.#images.forEach((image, i) => image.toggleAttribute('data-active', i === index));
    if (this.progress) this.progress.style.transform = `scaleX(${(index + 1) / this.#items.length})`;
    const href = this.#items[index]?.getAttribute('href');
    if (this.cta && href) this.cta.setAttribute('href', href);
  }
}

if (!customElements.get('s-style-scroller')) customElements.define('s-style-scroller', StyleScroller);
